import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { CircleDollarSign, Sparkles, History, CreditCard, Trash2 } from "lucide-react";
import { toast } from "sonner";

/**
 * AutopilotCard — resident-facing pre-authorisation for auto-paid dues up
 * to a chosen per-charge limit. Backed by Stripe SetupIntent + off-session
 * PaymentIntent (real charges).
 */
export function AutopilotCard() {
  const [rule, setRule] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [limit, setLimit] = useState("");
  const [pm, setPm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data }, { data: pmData }] = await Promise.all([
        api.get("/me/autopilot"),
        api.get("/me/autopilot/payment-method"),
      ]);
      setRule(data);
      setEnabled(!!data.enabled);
      setLimit(data.limit_per_charge ? String(data.limit_per_charge) : "");
      setPm(pmData);
    } catch (err) { if (typeof console !== 'undefined') console.debug('components/AutopilotCard.jsx: swallowed', err); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // Handle Stripe checkout redirect back with session_id
    const params = new URLSearchParams(window.location.search);
    const status = params.get("autopilot");
    const sid = params.get("session_id");
    if (status === "saved" && sid) {
      (async () => {
        try {
          await api.post("/me/autopilot/setup-callback", null, { params: { session_id: sid } });
          toast.success("Payment method saved");
          window.history.replaceState({}, "", window.location.pathname);
          await load();
        } catch (e) { toast.error(apiErrorMessage(e) || "Callback failed"); }
      })();
    } else if (status === "cancelled") {
      toast.warning("Payment method setup cancelled");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [load]);

  const save = async (nextEnabled) => {
    const n = Number(limit);
    if (nextEnabled && (!n || n <= 0)) {
      toast.error("Set a per-charge limit above $0 first.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/me/autopilot", {
        enabled: nextEnabled, limit_per_charge: n || 1,
      });
      toast.success(nextEnabled ? "Autopilot enabled" : "Autopilot disabled");
      setEnabled(nextEnabled);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Save failed"); }
    setBusy(false);
  };

  const saveLimit = async () => {
    const n = Number(limit);
    if (!n || n <= 0) return toast.error("Set a valid limit");
    setBusy(true);
    try {
      await api.post("/me/autopilot", {
        enabled, limit_per_charge: n,
      });
      toast.success("Limit updated");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e) || "Save failed"); }
    setBusy(false);
  };

  return (
    <Card className="p-6 mt-4 bg-white" data-testid="autopilot-card">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-2">
            <CircleDollarSign className="h-3.5 w-3.5" /> Payment autopilot
          </div>
          <div className="text-lg font-semibold font-display mt-1 flex items-center gap-2">
            {enabled ? "Autopilot is on" : "Autopilot is off"}
            {enabled && <Badge variant="outline" className="border-emerald-500/40 text-emerald-700 bg-emerald-500/10">Live</Badge>}
          </div>
          <div className="text-sm text-zinc-600 mt-1">
            Pre-authorise any HOA charge up to your limit. Approved dues sail through — no clicks needed.
          </div>
          <div className="mt-3 flex items-end gap-2">
            <div>
              <Label className="text-xs">Per-charge limit ($)</Label>
              <Input type="number" min="0" value={limit}
                     onChange={(e) => setLimit(e.target.value)}
                     placeholder="500"
                     className="w-32" data-testid="autopilot-limit" />
            </div>
            <Button size="sm" variant="outline" onClick={saveLimit}
                    disabled={busy || !limit}
                    data-testid="autopilot-save-limit">
              Save limit
            </Button>
          </div>
          <div className="text-[11px] text-zinc-500 mt-2 flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> Charges above the limit still need your one-tap approval.
          </div>

          <div className="mt-4 rounded-md border border-zinc-200 bg-zinc-50 p-3" data-testid="autopilot-pm-block">
            <div className="text-[11px] uppercase tracking-widest text-zinc-500 flex items-center gap-1 mb-2">
              <CreditCard className="h-3 w-3" /> Saved payment method
            </div>
            {pm?.has_payment_method ? (
              <div className="flex items-center justify-between text-sm">
                <div className="text-zinc-800 font-medium tabular-nums">
                  {pm.brand?.toUpperCase() || "CARD"} · **** {pm.last4}
                </div>
                <Button size="sm" variant="outline"
                        onClick={async () => {
                          if (!window.confirm("Remove saved payment method? Autopilot will stop until you add one again.")) return;
                          try {
                            await api.post("/me/autopilot/payment-method/detach");
                            toast.success("Payment method removed");
                            await load();
                          } catch (e) { toast.error(apiErrorMessage(e) || "Detach failed"); }
                        }}
                        data-testid="autopilot-pm-detach">
                  <Trash2 className="h-3 w-3 mr-1" /> Remove
                </Button>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div className="text-xs text-zinc-600">Add a card so autopilot can charge you off-session.</div>
                <Button size="sm"
                        onClick={async () => {
                          setBusy(true);
                          try {
                            const { data } = await api.post("/me/autopilot/setup-checkout", {
                              origin_url: window.location.origin,
                            });
                            window.location.href = data.checkout_url;
                          } catch (e) { toast.error(apiErrorMessage(e) || "Setup failed"); }
                          setBusy(false);
                        }}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid="autopilot-pm-add">
                  <CreditCard className="h-3 w-3 mr-1" /> Add card
                </Button>
              </div>
            )}
          </div>

          {enabled && (
            <div className={"text-[10px] rounded px-2 py-1 mt-3 " +
              (pm?.has_payment_method
                ? "text-emerald-700 bg-emerald-50 border border-emerald-200"
                : "text-amber-700 bg-amber-50 border border-amber-200")}
                 data-testid="autopilot-live-banner">
              {pm?.has_payment_method
                ? "LIVE: Eligible charges will be captured off-session with your saved card."
                : "Save a card above to activate real off-session charges. Until then, autopilot only queues charges (no real capture)."}
            </div>
          )}

          {rule?.history?.length > 0 && (
            <div className="mt-4 rounded-md border border-zinc-200 bg-zinc-50 p-3" data-testid="autopilot-history">
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 flex items-center gap-1 mb-2">
                <History className="h-3 w-3" /> Recent auto-approvals
              </div>
              <div className="space-y-1">
                {rule.history.slice(0, 5).map((h) => (
                  <div key={h.id} className="flex items-center justify-between text-xs">
                    <span className="text-zinc-600 tabular-nums">
                      {new Date(h.processed_at).toLocaleString()}
                    </span>
                    <span className="tabular-nums font-medium text-emerald-700">${Number(h.amount || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="shrink-0 flex flex-col items-end gap-2">
          <Switch
            data-testid="autopilot-toggle"
            checked={enabled}
            disabled={busy}
            onCheckedChange={save}
          />
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">
            {enabled ? "on" : "off"}
          </div>
        </div>
      </div>
    </Card>
  );
}

export default AutopilotCard;
